/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { getCanvasQuadsUnion } from "../selectors/pageHighlightsSelectors";
import { getPageZoomLevel } from "../selectors/commonAppSelectors";
import CanvasQuad from "../stores/CanvasQuad";
import { doQuadrilateralsOverlap, findDistanceBwPoints } from "./GeometryUtils";

export const isPointInsideCanvasQuadUnion = (stores, pageIndex, xval, yval) => {
  let x = xval;
  let y = yval;
  const quadsUnion = getCanvasQuadsUnion(stores, pageIndex);
  // scale back coords to 1 zoom level, since we store all rects at 1 zoomlevel.
  x /= getPageZoomLevel(stores, pageIndex);
  y /= getPageZoomLevel(stores, pageIndex); // no need since we are already storing at one zoomlevel
  return (
    quadsUnion &&
    x >= Math.min(quadsUnion.left, quadsUnion.right) &&
    x <= Math.max(quadsUnion.left, quadsUnion.right) &&
    y >= Math.min(quadsUnion.top, quadsUnion.bottom) &&
    y <= Math.max(quadsUnion.top, quadsUnion.bottom)
  );
};

/**
 * Unflattens an array by grouping its elements into subarrays of a specified block size.
 * @param {Array} arr - The flattened array to unflatten.
 * @param {number} blockSize - The size of each subarray (default is 2).
 * @returns {Array} - The unflattened array.
 */
export const unflattenArray = (arr, blockSize = 2) => {
  const clonedArray = arr.slice();
  const finalArr = [];
  while (clonedArray.length) finalArr.push(clonedArray.splice(0, blockSize));
  return finalArr;
};

const getLeftEdgeMidPoint = canvasQuad => {
  const { topLeft, bottomLeft } = canvasQuad;
  const leftMidPoint = {
    x: (topLeft.x + bottomLeft.x) / 2,
    y: (topLeft.y + bottomLeft.y) / 2
  };
  return leftMidPoint;
};

const getRightEdgeMidPoint = canvasQuad => {
  const { topRight, bottomRight } = canvasQuad;
  const rightMidPoint = {
    x: (topRight.x + bottomRight.x) / 2,
    y: (topRight.y + bottomRight.y) / 2
  };
  return rightMidPoint;
};

const quadMergeDirectionEnum = {
  LEFT: "left",
  RIGHT: "right"
};

/**
 * This function tells us whether we should merge given quads or not. The distnace bw two quads is calculated as
 * the minimum distance bw two corners & centre of one edge from the other edge. The edges in question are right edge
 * of left quad & left edge of right quad. The quads are considered to be in left-right order. ie
 * quad1 is considered to be on left and quad2 on right.
 * Quads received from docAPI (ie from T5) are generally in this order only but may sometimes be in reverse order.
 *
 * It returns an object with boolean for shouldMerge and for the direction of merge if two adjacent quads have distance
 * less than the threshold distance ( default 2 px).
 * @param {Object} quad1 CanvasQuad Object
 * @param {Object} quad2 CanvasQuad Object
 * @returns {Object}
 * {
 * shouldMerge: (boolean),
 * direction: (string) 'left' or 'right'
 * }
 */
const shouldMergeQuads = (quad1, quad2, thresholdDistance = 2) => {
  let shouldMerge = false;
  const { LEFT, RIGHT } = quadMergeDirectionEnum;

  /*
     Check if the quads are in left-right order, ie
     quad1 is on left and quad2 is on right.
     Find distances bw right side of 1st quad and left side of 2nd quad.
  */

  // 1. Find distance bw midpoints of right edge of 1st quad and left edge of 2nd quad.
  let distBwMidPoints = findDistanceBwPoints(
    getRightEdgeMidPoint(quad1),
    getLeftEdgeMidPoint(quad2)
  );
  // 2. Find distance bw top right of 1st quad and top left of 2nd quad.
  let distBwAdjacentTop = findDistanceBwPoints(quad1.topRight, quad2.topLeft);
  // 3. Find distance bw bottom right of 1st quad and bottom left of 2nd quad.
  let distBwAdjacentBottom = findDistanceBwPoints(
    quad1.bottomRight,
    quad2.bottomLeft
  );
  // should merge if any of the points are within the threshold
  shouldMerge =
    distBwAdjacentBottom <= thresholdDistance ||
    distBwMidPoints <= thresholdDistance ||
    distBwAdjacentTop <= thresholdDistance;
  if (shouldMerge)
    return {
      shouldMerge,
      direction: RIGHT,
      minDistance: Math.min(
        distBwAdjacentBottom,
        distBwMidPoints,
        distBwAdjacentTop
      )
    };

  /*
     Check if the quads are in right-left order, ie
     quad1 is on right and quad2 is on left.
     Find distances bw left side of 1st quad and right side of 2nd quad.
  */
  // 1. Find distance bw midpoints of left edge of 1st quad and right edge of 2nd quad.
  distBwMidPoints = findDistanceBwPoints(
    getLeftEdgeMidPoint(quad1),
    getRightEdgeMidPoint(quad2)
  );
  // 2. Find distance bw top left of 1st quad and top right of 2nd quad.
  distBwAdjacentTop = findDistanceBwPoints(quad1.topLeft, quad2.topRight);
  // 3. Find distance bw bottom left of 1st quad and bottom right of 2nd quad.
  distBwAdjacentBottom = findDistanceBwPoints(
    quad1.bottomLeft,
    quad2.bottomRight
  );
  // should merge if any of the points are within the threshold
  shouldMerge =
    distBwAdjacentBottom <= thresholdDistance ||
    distBwMidPoints <= thresholdDistance ||
    distBwAdjacentTop <= thresholdDistance;
  return {
    shouldMerge,
    direction: LEFT,
    minDistance: Math.min(
      distBwAdjacentBottom,
      distBwMidPoints,
      distBwAdjacentTop
    )
  };
};

const getOverlappingQuadIndexWithCaret = (mergedQuads, caretQuads) => {
  const mergedQuadsCopy = mergedQuads.slice();
  const lastQuad = mergedQuads[mergedQuads.length - 1];
  if (doQuadrilateralsOverlap(lastQuad.quadPoints, caretQuads.quadPoints)) {
    return mergedQuads.length - 1;
  }
  // eslint-disable-next-line no-restricted-syntax
  let index = 0;
  // eslint-disable-next-line no-restricted-syntax
  for (const quad of mergedQuadsCopy) {
    if (doQuadrilateralsOverlap(quad.quadPoints, caretQuads.quadPoints)) {
      return index;
    }
    index += 1;
  }
  return index;
};

const exhaustiveQuadMergeHorizontal = (
  orderedCanvasQuads,
  shouldMergeCallback,
  thresholdDistance,
  caretQuads
) => {
  let initialQuads = orderedCanvasQuads.slice();
  const mergedQuads = initialQuads.slice();

  /**
   * This function compares the given quad with all the quads in the array
   * and returns the info of the index of the quad with minimum distance from
   * the given quad.
   */
  const getMinDistanceIndex = (quadArr, initialIndex, quadToCompare) => {
    const minDistanceInfo = {
      indexToMerge: -1,
      minDistance: Number.MAX_SAFE_INTEGER,
      direction: null,
      shouldMerge: false
    };
    let arrIndex = initialIndex;
    // Checking the given quad with all the quads in the array starting from initialIndex
    // as we assume that previous indices are already merged using this approach
    while (arrIndex < quadArr.length) {
      const quad2 = quadArr[arrIndex];
      const { shouldMerge, direction, minDistance } = shouldMergeCallback(
        quadToCompare,
        quad2,
        thresholdDistance
      );
      if (shouldMerge && minDistanceInfo.minDistance > minDistance) {
        minDistanceInfo.minDistance = minDistance;
        minDistanceInfo.indexToMerge = arrIndex;
        minDistanceInfo.direction = direction;
        minDistanceInfo.shouldMerge = shouldMerge;
      }
      arrIndex += 1;
    }
    return minDistanceInfo;
  };

  do {
    initialQuads = mergedQuads.slice();
    let index = 0;
    while (index < mergedQuads.length) {
      const quad1 = mergedQuads[index];
      const minDistanceInfo = getMinDistanceIndex(
        mergedQuads,
        index + 1,
        quad1
      );
      if (minDistanceInfo.shouldMerge) {
        const { RIGHT } = quadMergeDirectionEnum;
        const quad2 = mergedQuads[minDistanceInfo.indexToMerge];
        const latestMergedQuad =
          minDistanceInfo.direction === RIGHT
            ? quad1.mergeQuad(quad2)
            : quad2.mergeQuad(quad1);
        mergedQuads[index] = latestMergedQuad;
        mergedQuads.splice(minDistanceInfo.indexToMerge, 1); // remove the element at innerIndex as it is merged with element at index
      }
      /*
        NOTE:
        Incrementing index to check the next quad even if there was merge happening in the previous iteration
        This is done to improve perf so that we don't end up interating n^2 times in case all the quads are mergable
        This way we will end up iterating n times the first time, then n/2, then n/4 and so on.
      */
      index += 1;
    }
  } while (initialQuads.length !== mergedQuads.length);
  if (caretQuads) {
    const quadIndex = getOverlappingQuadIndexWithCaret(mergedQuads, caretQuads);
    let newLastQuad;
    if (quadIndex >= mergedQuads.length) {
      newLastQuad = caretQuads;
    } else {
      const overlappingQuad = mergedQuads[quadIndex];
      newLastQuad = overlappingQuad.getEffectiveCaretQuad(caretQuads);
      // Remove element at indexToRemove
      mergedQuads.splice(quadIndex, 1);
    }
    // Push this element at the end so that it can be merged with caret
    mergedQuads.push(newLastQuad);
  }
  return mergedQuads;
};

export const exhaustiveQuadMergeVertical = (
  orderedCanvasQuads,
  shouldMergeCallback,
  thresholdDistance
) => {
  let initialQuads = orderedCanvasQuads.slice();
  const mergedQuads = initialQuads.slice();

  /*
    This function compares the given quad with all the intersecting quads in the array
    until a first non-intersecting quad is found starting from quad at initialIndex.
    The function returns the info of the index of the quad with maximum area.
  */
  const getMaxAreaIntersectingQuad = (
    quadArr,
    initialIndex,
    quadToCompare,
    previousMinDistanceInfo
  ) => {
    const finalMinDistanceInfo = {
      ...previousMinDistanceInfo
    };
    let intersectingQuadIndex = initialIndex;
    let currentMinDistance = finalMinDistanceInfo.minDistance;

    while (intersectingQuadIndex < quadArr.length) {
      const nextIntersectingQuad = quadArr[intersectingQuadIndex];
      const currentMergeInfo = shouldMergeCallback(
        quadToCompare,
        nextIntersectingQuad,
        thresholdDistance
      );
      if (!currentMergeInfo.shouldMerge) {
        break;
      }
      currentMinDistance = currentMergeInfo.minDistance;

      // If we find a non-intersecting quad, then stop looking further
      if (currentMinDistance !== 0) {
        break;
      }

      // next quad is also intersecting, now checking if it has more area than the current one
      if (nextIntersectingQuad.area > finalMinDistanceInfo.maxArea) {
        finalMinDistanceInfo.maxArea = nextIntersectingQuad.area;
        finalMinDistanceInfo.indexToMerge = intersectingQuadIndex;
      }
      intersectingQuadIndex += 1;
    }

    return finalMinDistanceInfo;
  };

  /**
   * This function compares the given quad with all the quads in the array
   * and returns the info of the index of the quad with minimum distance from
   * the given quad.
   * If a quad is intersected, then it continues until it finds a quad which is not intersected.
   * Then out of those intersected quads, the largest quad is selected for merging.
   */
  const getMinDistanceIndex = (quadArr, initialIndex, indexToCompare) => {
    const minDistanceInfo = {
      indexToMerge: -1,
      minDistance: Number.MAX_SAFE_INTEGER,
      maxArea: Number.MIN_SAFE_INTEGER,
      shouldMerge: false
    };
    let arrIndex = initialIndex;
    const quad1 = quadArr[indexToCompare];
    // Checking the given quad with all the quads in the array starting from initialIndex
    // as we assume that previous indices are already merged using this approach
    while (arrIndex < quadArr.length) {
      const quad2 = quadArr[arrIndex];
      const { shouldMerge, minDistance } = shouldMergeCallback(
        quad1,
        quad2,
        thresholdDistance
      );

      if (shouldMerge) {
        if (minDistance === 0) {
          // quads are intersecting as the distance is 0
          minDistanceInfo.maxArea = quad2.area;
          minDistanceInfo.indexToMerge = arrIndex;
          minDistanceInfo.shouldMerge = shouldMerge;
          minDistanceInfo.minDistance = 0;

          // check if any of the next quads are also intersecting
          // if yes, then check if they have more area than the current one and select it for merging
          // and return the info
          return getMaxAreaIntersectingQuad(
            quadArr,
            arrIndex + 1,
            quad1,
            minDistanceInfo
          );
        } else if (minDistanceInfo.maxArea < quad2.area) {
          minDistanceInfo.maxArea = quad2.area;
          minDistanceInfo.indexToMerge = arrIndex;
          minDistanceInfo.shouldMerge = shouldMerge;
          minDistanceInfo.minDistance = minDistance;
        }
      }
      arrIndex += 1;
    }
    return minDistanceInfo;
  };

  do {
    initialQuads = mergedQuads.slice();
    let index = 0;
    while (index < mergedQuads.length) {
      const minDistanceInfo = getMinDistanceIndex(
        mergedQuads,
        index + 1,
        index
      );
      const { shouldMerge, indexToMerge } = minDistanceInfo;
      if (shouldMerge) {
        const quad1 = mergedQuads[index];
        const quad2 = mergedQuads[indexToMerge];
        const latestMergedQuad = quad1.mergeQuad(quad2);
        mergedQuads[index] = latestMergedQuad;
        mergedQuads.splice(indexToMerge, 1); // remove the element at innerIndex as it is merged with element at index
      }
      /* 
        NOTE:
        Incrementing index to check the next quad even if there was merge happening in the previous iteration
        This is done to improve perf so that we don't end up interating n^2 times in case all the quads are mergable
        This way we will end up iterating n times the first time, then n/2, then n/4 and so on.
      */
      index += 1;
    }
  } while (initialQuads.length !== mergedQuads.length);

  return mergedQuads;
};

/**
 * This function merges the given quads array based on given callback function
 * to decide whether to merge the quads or not.
 * The quads in array should be in order from left to right and in View Space Coordinates.
 * @param {Array} quadPointsArr array of canvasQuads. If the array is not a CanvasQuad array,
 * it is converted to canvasQuads by using the given array elements as params for CanvasQuad class
 * @param {function} shouldMergeCallback this function takes 2 params : last merged quad and current quad.
 * It returns true if the given quads are to be merged.
 * @returns {Array} array of merged canvasQuads
 */
export function mergeQuads(
  quadPointsArr,
  shouldMergeCallback = shouldMergeQuads,
  thresholdDistance = 2
) {
  const mergedQuads = [];
  if (Array.isArray(quadPointsArr) && quadPointsArr.length) {
    let canvasQuads = quadPointsArr;

    // assuming if one element is a canvas quad then all of the elements are canvas quads
    const isFirstElementCanvasQuad = quadPointsArr[0] instanceof CanvasQuad;
    if (!isFirstElementCanvasQuad) {
      canvasQuads = quadPointsArr.map(
        quadPointsObj => new CanvasQuad(quadPointsObj)
      );
    }

    // merge the canvas quads array
    canvasQuads.forEach(quad => {
      const isFirstElement = mergedQuads.length === 0;
      if (isFirstElement) {
        mergedQuads.push(quad);
      } else {
        const lastIndex = mergedQuads.length - 1;
        const lastQuad = mergedQuads[lastIndex];
        const { shouldMerge } = shouldMergeCallback(
          lastQuad,
          quad,
          thresholdDistance
        );
        if (shouldMerge) {
          mergedQuads[lastIndex] = lastQuad.mergeQuad(quad);
        } else {
          mergedQuads.push(quad);
        }
      }
    });
  }

  return mergedQuads;
}

export function mergeQuadsExhaustively(
  quadPointsArr,
  shouldMergeCallback = shouldMergeQuads,
  thresholdDistance = 2,
  caretQuads
) {
  const mergedQuads = [];
  if (Array.isArray(quadPointsArr) && quadPointsArr.length) {
    let canvasQuads = quadPointsArr;

    // assuming if one element is a canvas quad then all of the elements are canvas quads
    const isFirstElementCanvasQuad = quadPointsArr[0] instanceof CanvasQuad;
    if (!isFirstElementCanvasQuad) {
      canvasQuads = quadPointsArr.map(
        quadPointsObj => new CanvasQuad(quadPointsObj)
      );
    }
    const finalQuads = exhaustiveQuadMergeHorizontal(
      canvasQuads,
      shouldMergeCallback,
      thresholdDistance,
      caretQuads
    );
    return finalQuads;
  }

  return mergedQuads;
}
